
  /* eslint-disable no-restricted-globals */
  import {
    Ref,
    defineComponent,
    onMounted,
    ref,
    useContext,
    useRouter
  } from "@nuxtjs/composition-api";
  import { email, required } from "vuelidate/lib/validators";
  import { errors } from "../use/vuelidate";
  import {
    getAuth,
    isSignInWithEmailLink,
    onAuthStateChanged
  } from "firebase/auth";
  import {
    useCurrentUser,
    useCurrentUserProvide,
    useTrackingUser
  } from "../use/useFireAuth";
  import { useGlobalProvider } from "../use/useGlobal";
  import { useInternetTradingPolicyProvide } from "../use/useInternetTradingPolicy";
  import { useOverlayProvide } from "../use/useOverlay";
  import { useSiteConfigurationProvide } from "../use/useSiteConfiguration";
  import firebaseApp from "../plugins/firebase";
  import useAuthStore from "../stores/auth/auth.store";
  import useGTM from "~/use/useGTM";

  export default defineComponent({
    setup() {
      const $fireAuth = getAuth(firebaseApp);
      const authStore = useAuthStore();
      const context = useContext();
      const { route, $config } = context;
      const router = useRouter();

      const { internetTradingPolicy } = useSiteConfigurationProvide(context);

      useOverlayProvide();
      const {
        login,
        loginInProgress,
        loggedEmail,
        authUser,
        logout,
        userPolicies
      } = useCurrentUserProvide(context);
      const { completeSignInWithEmail } = useCurrentUser(context);

      // initializing the internet trading policy context
      const { showITP } = useInternetTradingPolicyProvide(
        internetTradingPolicy,
        userPolicies
      );
      const {
        isGlobalAlertShow,
        globalAlertMessage,
        globalAlertType,
        hideGlobalAlert,
        globalAlertTimeout,
        globalBanner,
        showGlobalBanner,
        hideGlobalBanner,
        globalDialogShareAccess,
        showGlobalDialogShareAccess
      } = useGlobalProvider();
      const { pushEvent } = useGTM(context);
      const { uid, newPartnerType } = useTrackingUser();

      const drawer: Ref<any> = ref(null);
      const show: Ref<
        | "splash"
        | "signIn"
        | "signInAskForEmail"
        | "accessGranted"
        | "unsubscribe"
      > = ref("splash");
      const accessDenied = ref("");

      const startPage: Ref<string> = ref("/dashboard");
      const signInUrl: Ref<string | undefined> = ref(undefined);

      function isDirectPurchase(path: string) {
        return path.startsWith("/insureLoad/directBuyV1");
      }

      function getStartPage(path: string, fullPath: string, href: string) {
        if (path === "/") {
          return "/dashboard";
        }
        if (isSignInWithEmailLink($fireAuth, href)) {
          if (path === "/") {
            return "/dashboard";
          }
          return path;
        }
        return fullPath;
      }

      async function onLogout() {
        const userId = uid.value;
        const partnerType = newPartnerType.value;
        show.value = "splash";
        authStore.resetAuthState();
        await logout();
        window.localStorage.removeItem("startPage");
        window.localStorage.removeItem("isLoggedIn");
        pushEvent("logout", {
          user_id: userId,
          partner_type: partnerType
        });
        startPage.value = "/dashboard";
        if (route.value.path !== "/") {
          if (process.client && route.value.path) {
            router.push("/");
          }
        }
      }

      onMounted(async () => {
        if (route.value.path === "/unsubscribe/") {
          show.value = "unsubscribe";
          return;
        }
        if (isDirectPurchase(route.value.path)) {
          show.value = "accessGranted";
          return;
        }

        const isAllowedHost = ["loadsure.net", "localhost"].some((hostname) =>
          window.location.hostname.includes(hostname)
        );
        if (!isAllowedHost) {
          window.location.replace(
            window.location
              .toString()
              .replace(window.location.origin, $config.baseUrl)
          );
        }

        startPage.value = getStartPage(
          route.value.path,
          route.value.fullPath,
          // eslint-disable-next-line no-restricted-globals
          location.href
        );

        if (isSignInWithEmailLink($fireAuth, location.href)) {
          const emailSignIn = window.localStorage.getItem("emailForSignIn");
          startPage.value =
            window.localStorage.getItem("startPage") ?? "/dashboard";
          if (emailSignIn) {
            await completeSignInWithEmail(emailSignIn, location.href);
          } else {
            signInUrl.value = location.href;
            show.value = "signInAskForEmail";
          }
        } else {
          window.localStorage.setItem("startPage", startPage.value);
        }

        onAuthStateChanged($fireAuth, async (newAuthUser) => {
          if (route.value.path !== "/unsubscribe/") {
            accessDenied.value = "";
            if (newAuthUser) {
              const success = await login(newAuthUser);
              if (!success) {
                accessDenied.value = newAuthUser.email || "unknown";
                show.value = "signIn";
                if (route.value.path === "/unsubscribe/") {
                  show.value = "unsubscribe";
                }
              } else {
                if (!window.localStorage.getItem("isLoggedIn")) {
                  pushEvent("login", {});
                  window.localStorage.setItem("isLoggedIn", "true");
                }

                if (route.value.fullPath !== startPage.value) {
                  if (process.client && startPage.value) {
                    router.push(startPage.value);
                  }
                }

                show.value = "accessGranted";
              }
            } else if (show.value !== "signInAskForEmail") {
              if (route.value.path === "/unsubscribe/") {
                show.value = "unsubscribe";
              } else {
                show.value = "signIn";
              }
            }
          } else {
            show.value = "unsubscribe";
          }
        });
      });

      const year = new Date().getFullYear();
      const shortYear = year.toString().slice(-2);

      return {
        accessDenied,
        loginInProgress,
        authUser,
        loggedEmail,
        drawer,
        signInUrl,
        onLogout,
        show,
        showITP,
        shortYear,
        isGlobalAlertShow,
        globalAlertMessage,
        globalAlertType,
        hideGlobalAlert,
        globalAlertTimeout,
        globalBanner,
        showGlobalBanner,
        hideGlobalBanner,
        globalDialogShareAccess,
        showGlobalDialogShareAccess
      };
    },
    validations: {
      globalDialogShareAccess: {
        email: {
          required,
          email
        }
      }
    },
    computed: {
      globalDialogShareAccessEmailError() {
        return errors(
          this.$v.globalDialogShareAccess.email,
          "globalDialogShareAccessEmail",
          {
            required: "This field is required"
          }
        );
      }
    }
  });
